import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import VerifiedBadge from "@/components/VerifiedBadge";
import ProBadge from "@/components/ProBadge";
import FoundingMemberBadge from "@/components/FoundingMemberBadge";
import BusinessShareCard from "@/components/BusinessShareCard";
import PromotionsCard from "@/components/PromotionsCard";
import BirthdayPromoCard from "@/components/BirthdayPromoCard";
import BusinessLocationPingCard from "@/components/BusinessLocationPingCard";
import InboxBroadcastCard from "@/components/InboxBroadcastCard";
import PushRegistrationCard from "@/components/PushRegistrationCard";
import ReferralProgramCard from "@/components/ReferralProgramCard";
import LoyaltyProgramCard from "@/components/LoyaltyProgramCard";
import MarketingReportCard from "@/components/MarketingReportCard";
import RedemptionsLogCard from "@/components/RedemptionsLogCard";
import SuggestionForm from "@/components/SuggestionForm";
import FlaggedCheckInsCard from "@/components/FlaggedCheckInsCard";
import InviteCustomerCard from "@/components/InviteCustomerCard";
import BusinessEmbedWidgetCard from "@/components/BusinessEmbedWidgetCard";
import { Lock, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function BusinessDashboard() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [written, setWritten] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: "", last_name: "", street: "", city: "", state: "", email: "" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    refresh();
    api.get("/me/reviews/written").then(({ data }) => setWritten(data));
  }, []); // eslint-disable-line

  const [results, setResults] = useState([]);
  const lookup = async (e) => {
    e.preventDefault();
    setSearching(true);
    setResult(null);
    setResults([]);
    setNotFound(false);
    setAddOpen(false);
    try {
      const { data } = await api.post("/customers/lookup", { name, street: "", city: "" });
      if (!data.found) {
        setNotFound(true);
        const parts = name.trim().split(/\s+/);
        const first = parts.shift() || "";
        const last = parts.join(" ");
        setAddForm({ first_name: first, last_name: last, street: "", city: "", state: "", email: "" });
      } else if (data.customer) {
        setResult(data.customer);
      } else if (Array.isArray(data.matches)) {
        if (data.matches.length === 1) setResult(data.matches[0]);
        else setResults(data.matches);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSearching(false);
    }
  };

  const addUpdate = (k) => (e) => setAddForm({ ...addForm, [k]: e.target.value });

  const addCustomer = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const payload = { ...addForm };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/customers/add", payload);
      toast.success(data.created ? "Customer added — leave them a review" : "Customer already on file — opening profile");
      setResult(data.customer);
      setNotFound(false);
      setAddOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdding(false);
    }
  };

  const active = user?.subscription_active;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gray-500">Business console</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2 inline-flex items-center gap-2 flex-wrap">
            <span>{user?.business_name}</span>
            {user?.subscription_active && <ProBadge size={20} showLabel />}
            {user?.is_verified && <VerifiedBadge size={22} showLabel />}
            {user?.is_founding_member && <FoundingMemberBadge size={14} showLabel />}
          </h1>
          <p className="text-gray-600 mt-2">{user?.business_address}</p>
        </div>
        <div className="flex items-center gap-3">
          {active ? (
            <Badge className="rounded-sm bg-emerald-500 hover:bg-emerald-500 text-white">Subscription active</Badge>
          ) : (
            <Badge variant="outline" className="rounded-sm border-amber-300 text-amber-700">No active subscription</Badge>
          )}
          <Link to="/dashboard/business/edit">
            <Button variant="outline" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="edit-listing-button">
              Edit listing
            </Button>
          </Link>
          <Link to="/subscription">
            <Button variant={active ? "outline" : "default"} className={`rounded-[35px] shadow-sm hover:shadow-md ${active ? "" : "bg-[#B31E1E] hover:bg-[#8F1717]"}`} data-testid="upgrade-subscription-button">
              {active ? "Manage" : "Subscribe — $9.95/mo"}
            </Button>
          </Link>
        </div>
      </div>

      {/* PRIORITY BLOCK — Marketing ROI → Special Offers → Inbox Broadcasts */}
      {user?.marketing_active && (
        <div className="mt-10" data-testid="dashboard-marketing-roi-top">
          <MarketingReportCard />
        </div>
      )}
      <div className="mt-10" data-testid="dashboard-promotions-top">
        <PromotionsCard marketingActive={user?.marketing_active} />
      </div>
      <div className="mt-10" data-testid="dashboard-inbox-broadcast-top">
        <InboxBroadcastCard subscriptionActive={!!user?.subscription_active} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-md border border-border p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Search size={18} />
            <h3 className="font-display text-2xl">Look up a customer</h3>
          </div>
          <form onSubmit={lookup} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:items-end">
            <div>
              <Label htmlFor="lk-name">Customer name</Label>
              <Input id="lk-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="First or last name" className="rounded-sm mt-1" data-testid="lookup-name-input" />
            </div>
            <div>
              <Button type="submit" disabled={searching} className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717] w-full sm:w-auto" data-testid="lookup-submit-button">
                {searching ? "Searching..." : "Find customer"}
              </Button>
            </div>
          </form>

          {notFound && !result && active && (
            <div className="mt-6 border-t border-border pt-6" data-testid="customer-not-found">
              <p className="text-sm text-gray-700">
                No customer matched <strong>{name}</strong>.
              </p>
              {!addOpen ? (
                <Button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="mt-3 rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
                  data-testid="add-customer-open"
                >
                  Add this customer to the ledger →
                </Button>
              ) : (
                <form onSubmit={addCustomer} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="add-customer-form">
                  <div>
                    <Label htmlFor="ac-first">First name</Label>
                    <Input id="ac-first" required value={addForm.first_name} onChange={addUpdate("first_name")} className="rounded-md mt-1" data-testid="add-customer-first" />
                  </div>
                  <div>
                    <Label htmlFor="ac-last">Last name</Label>
                    <Input id="ac-last" required value={addForm.last_name} onChange={addUpdate("last_name")} className="rounded-md mt-1" data-testid="add-customer-last" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="ac-street">Street address</Label>
                    <Input id="ac-street" required placeholder="42 Oak Lane" value={addForm.street} onChange={addUpdate("street")} className="rounded-md mt-1" data-testid="add-customer-street" />
                  </div>
                  <div>
                    <Label htmlFor="ac-city">City</Label>
                    <Input id="ac-city" required value={addForm.city} onChange={addUpdate("city")} className="rounded-md mt-1" data-testid="add-customer-city" />
                  </div>
                  <div>
                    <Label htmlFor="ac-state">State</Label>
                    <Input id="ac-state" required placeholder="CA" value={addForm.state} onChange={addUpdate("state")} className="rounded-md mt-1 max-w-[140px]" data-testid="add-customer-state" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="ac-email">Email <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <Input
                      id="ac-email"
                      type="email"
                      placeholder="customer@example.com"
                      value={addForm.email}
                      onChange={addUpdate("email")}
                      className="rounded-md mt-1"
                      data-testid="add-customer-email"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Optional — if provided, we can message this customer your promotions (with their consent). You&apos;ll never see their contact address after this; we send on your behalf.
                    </p>
                  </div>
                  <div className="md:col-span-2 flex gap-2">
                    <Button type="submit" disabled={adding} className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="add-customer-submit">
                      {adding ? "Adding…" : "Add customer"}
                    </Button>
                    <Button type="button" variant="ghost" className="rounded-[35px]" onClick={() => setAddOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                  <p className="md:col-span-2 text-[11px] text-gray-500">
                    Customers added by businesses appear as unclaimed. They can later register with the same name + address to claim their profile.
                  </p>
                </form>
              )}
            </div>
          )}

          {notFound && !result && !active && (
            <div className="mt-6 border-t border-border pt-6" data-testid="customer-not-found-locked">
              <p className="text-sm text-gray-700">
                No customer matched <strong>{name}</strong>.
              </p>
              <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Subscribe to servrevu Pro to add new customers to the ledger and leave reviews.
              </p>
            </div>
          )}

          {result && (
            <div className="mt-6 border-t border-border pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-display text-xl" data-testid="lookup-result-name">{result.name}</h4>
                  <p className="text-sm text-gray-500">{result.address}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StarRating value={result.rating_avg} />
                  <span className="text-sm text-gray-500">{result.rating_count} reviews</span>
                </div>
              </div>
              <div className="mt-4">
                <Link to={`/customers/${result.id}`}>
                  <Button variant="outline" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="lookup-view-customer-button">View customer profile →</Button>
                </Link>
              </div>
            </div>
          )}

          {!result && results.length > 0 && (
            <div className="mt-6 border-t border-border pt-6" data-testid="lookup-results-list">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <p className="text-sm text-gray-700">
                  Found <strong>{results.length}</strong> matching customer{results.length === 1 ? "" : "s"}.
                </p>
                <p className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Click a row to open the profile</p>
              </div>
              <ul className="space-y-2">
                {results.map((c) => (
                  <li key={c.id} data-testid={`lookup-result-${c.id}`}>
                    <Link
                      to={`/customers/${c.id}`}
                      className="block border border-border rounded-md p-4 hover:border-[#B31E1E] hover:bg-[#B31E1E]/[0.02] transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <h4 className="font-display text-lg truncate">{c.name}</h4>
                          <p className="text-sm text-gray-500 truncate">{c.address || "—"}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StarRating value={c.rating_avg} />
                          <span className="text-sm text-gray-500">{c.rating_count} review{c.rating_count === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card className="rounded-sm border border-border p-6">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} />
            <h3 className="font-display text-xl">Why subscribe?</h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            Customer reviews from other businesses stay locked behind a paywall. Active subscribers can read every review, post their own, and build the ledger together.
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            <li className="flex gap-2"><span className="text-[#B31E1E]">→</span> Read every review on any customer</li>
            <li className="flex gap-2"><span className="text-[#B31E1E]">→</span> Post reviews on customers</li>
            <li className="flex gap-2"><span className="text-[#B31E1E]">→</span> Browse the gated customer directory</li>
            <li className="flex gap-2"><span className="text-[#B31E1E]">→</span> Your business listing stays free either way</li>
          </ul>
        </Card>
      </div>

      {/* QR + share-link card */}
      <div className="mt-10">
        <BusinessShareCard business={user} />
      </div>

      {/* Website embed widget generator */}
      {user?.id && (
        <div className="mt-10">
          <BusinessEmbedWidgetCard businessId={user.id} />
        </div>
      )}

      {/* Invite a friend to Servrevu */}
      <div className="mt-10">
        <InviteCustomerCard />
      </div>

      {/* Marketing ROI + Special Offers + Inbox Broadcasts moved to the top
          of this page (see the priority block above the customer lookup). */}

      {/* Unified redemptions audit log — collapsed by default, expands on click */}
      <div className="mt-10">
        <RedemptionsLogCard />
      </div>

      {/* Birthday promotions */}
      <div className="mt-10">
        <BirthdayPromoCard
          marketingActive={!!user?.marketing_active}
          subscriptionActive={!!user?.subscription_active}
        />
      </div>

      {/* Mobile business — live location ping */}
      <div className="mt-10">
        <BusinessLocationPingCard
          marketingActive={!!user?.marketing_active}
          subscriptionActive={!!user?.subscription_active}
        />
      </div>

      {/* Push notifications */}
      <div className="mt-10">
        <PushRegistrationCard />
      </div>

      {/* Referral program */}
      <div className="mt-10">
        <ReferralProgramCard marketingActive={!!user?.marketing_active} />
      </div>

      {/* Loyalty program */}
      <div className="mt-10">
        <LoyaltyProgramCard marketingActive={!!user?.marketing_active} />
      </div>

      {/* Flagged check-ins (Strict Mode soft-warn) */}
      <div className="mt-10">
        <FlaggedCheckInsCard />
      </div>

      {/* Verified Pro checklist */}
      <div className="mt-10">
        <Card className="rounded-md border border-border p-6" data-testid="verified-pro-card">
          <div className="flex items-center gap-2 mb-2">
            <VerifiedBadge size={18} />
            <h3 className="font-display text-xl">Earn the Verified Pro check</h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">
            Customers click on businesses with a blue Verified Pro check 3–4x more often than unlabelled listings. Complete the four items below and the badge appears automatically next to your business name.
          </p>
          {(() => {
            const items = [
              { key: "claimed", label: "Listing claimed", done: !!user?.claimed },
              { key: "review", label: "At least one customer review", done: (user?.rating_count || 0) >= 1 },
              { key: "logo", label: "Logo uploaded", done: !!user?.logo_data_url },
              { key: "gallery", label: "At least one work photo in your gallery", done: (user?.gallery || []).length > 0 },
            ];
            const done = items.filter((i) => i.done).length;
            return (
              <>
                <div className="mt-5 flex items-center gap-3">
                  <div className="h-2 flex-1 max-w-md rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-[#1e88e5] transition-all"
                      style={{ width: `${(done / items.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-gray-500">{done} / {items.length}</span>
                </div>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {items.map((it) => (
                    <li
                      key={it.key}
                      className={`flex items-center gap-2 ${it.done ? "text-gray-800" : "text-gray-500"}`}
                      data-testid={`verified-checklist-${it.key}`}
                    >
                      <span
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${
                          it.done ? "bg-[#1e88e5] text-white" : "bg-gray-100 text-gray-400 border border-gray-200"
                        }`}
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span>{it.label}</span>
                    </li>
                  ))}
                </ul>
                {!user?.is_verified && (
                  <div className="mt-5">
                    <Link to="/dashboard/business/edit">
                      <Button variant="outline" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="verified-cta">
                        Finish setup →
                      </Button>
                    </Link>
                  </div>
                )}
                {user?.is_verified && (
                  <p className="mt-5 inline-flex items-center gap-2 text-sm text-[#1e88e5] font-medium" data-testid="verified-complete">
                    <VerifiedBadge size={16} /> You&apos;re Verified Pro — the badge is live on your public listing.
                  </p>
                )}
              </>
            );
          })()}
        </Card>
      </div>

      <div className="mt-10">
        <h3 className="overline text-gray-500">Reviews you&apos;ve written</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="written-reviews">
          {written.length === 0 && <p className="text-sm text-gray-500">You haven&apos;t reviewed any customers yet.</p>}
          {written.map((r) => (
            <Card key={r.id} className="rounded-sm border border-border p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-gray-500">{r.type === "business_on_customer" ? "On customer" : "On business"}</span>
                <StarRating value={r.rating} />
              </div>
              <p className="mt-2 text-sm">{r.comment}</p>
            </Card>
          ))}
        </div>
      </div>
      <div className="mt-10">
        <SuggestionForm />
      </div>
    </div>
  );
}
